import { DateTime, Duration } from "luxon";
import Enums from "@/enums";
import { toast } from "react-toastify";
import RightTimeApi from "@/API";
import { parse as parseJsonc, printParseErrorCode } from "jsonc-parser";
import { downloadAuthenticatedFile } from "@/utils/authenticatedFileDownload";
import { resolveApiBaseUrl } from "@/utils/apiBackend";


const Helpers = {
   pluralize(word, count) {
      // Your pluralization logic here
      if (count === 1) {
         return word;
      } else {
         return word + "s"; // Or your more complex logic
      }
   },

   AppVersion() {
      try {
         // console.log('Environment variables:', {
         //    VITE_APP_VERSION: import.meta.env.VITE_APP_VERSION,
         //    MODE: import.meta.env.MODE,
         //    DEV: import.meta.env.DEV,
         //    PROD: import.meta.env.PROD,
         //    allEnvVars: import.meta.env
         // });
         return import.meta.env.VITE_APP_VERSION || "Unknown";
      } catch (e) {
         console.error('Error getting app version:', e);
         return "0.0.0";
      }
   },

   formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
   },

   formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString();
   },

   // Helper function to format date for display (date only)
   formatDateOnly(datetime) {
      if (!datetime) return "";
      return Helpers.EST(datetime, "yyyy-MM-dd");
   },

   // Helper function to format full date/time for hover
   formatFullDateTime(datetime) {
      if (!datetime) return "";
      return Helpers.EST(datetime, "MM/dd/yyyy - hh:mm a");
   },

   createUTCDatetime(dateString) {
      if (!dateString) return null;

      // If it's already in ISO format with time, use it as-is
      if (dateString.includes('T')) {
         return new Date(`${dateString}${dateString.endsWith('Z') ? '' : 'Z'}`);
      }

      // For date-only strings
      let year, month, day;

      if (dateString.includes('-')) {
         const parts = dateString.split('-').map(p => p.trim());

         if (parts.length !== 3) {
            console.warn(`Invalid date format: ${dateString}`);
            return null;
         }

         if (parts[0].length === 4) {
            // Format: YYYY-MM-DD
            [year, month, day] = parts;
         } else {
            // Format: MM-DD-YYYY
            [month, day, year] = parts;
         }
      } else if (dateString.includes('/')) {
         // Handle MM/DD/YYYY format
         const parts = dateString.split('/').map(p => p.trim());

         if (parts.length !== 3) {
            console.warn(`Invalid date format: ${dateString}`);
            return null;
         }

         [month, day, year] = parts;
      } else {
         // Fallback to original behavior
         return new Date(`${dateString}T00:00:00.000Z`);
      }

      // Validate and parse components
      const yearNum = parseInt(year, 10);
      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);

      if (isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum) ||
         monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
         console.warn(`Invalid date components: ${dateString}`);
         return null;
      }

      // Create a date that when serialized to JSON will be exactly what we want
      // Use local date constructor but adjust for timezone offset
      const localDate = new Date(yearNum, monthNum - 1, dayNum, 0, 0, 0, 0);
      const timezoneOffsetMs = localDate.getTimezoneOffset() * 60 * 1000;

      // Adjust by the timezone offset to ensure the UTC representation is correct
      return new Date(localDate.getTime() + timezoneOffsetMs);
   },

   // Add this function to convert row numbers to ordinal words
   GetOrdinalWord(rowNum) {
      const ordinals = {
         1: "First",
         2: "Second",
         3: "Third",
         4: "Fourth",
         5: "Fifth",
         6: "Sixth",
         7: "Seventh",
         8: "Eighth",
         9: "Ninth",
         10: "Tenth"
      };
      return ordinals[rowNum] || `Row ${rowNum}`;
   },

   /**
    * Format an API timestamp in US Eastern. ISO strings without an offset/Z are treated as UTC
    * (RightTime API convention). Strings with an offset or Z are converted from that zone.
    */
   EST(datetime, format) {
      if (!format) format = "MM/dd/yyyy - hh:mm a";
      if (!datetime) return "";
      let dt;
      if (datetime instanceof Date) {
         dt = DateTime.fromJSDate(datetime, { zone: "utc" });
      } else {
         const raw = String(datetime).trim();
         const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
         dt = hasZone
            ? DateTime.fromISO(raw, { setZone: true })
            : DateTime.fromISO(raw, { zone: "utc" });
      }
      if (!dt.isValid) return "";
      return dt.setZone("America/New_York").toFormat(format);
   },

   DateDiffSeconds(start, end) {
      if (!start || !end) return "";
      const startDate = DateTime.fromISO(start, { zone: "utc" });
      const endDate = DateTime.fromISO(end, { zone: "utc" });
      const ret = endDate.diff(startDate, "seconds");
      return ret;
   },
   DateDiffFormat(start, end) {
      if (!start || !end) return "";
      const startDate = DateTime.fromISO(start, { zone: "utc" });
      const endDate = DateTime.fromISO(end, { zone: "utc" });
      let duration = endDate.diff(startDate, "seconds");

      const hours = Math.floor(duration.as('hours'));
      const span = Duration.fromObject({
         hours: hours,
         minutes: Math.floor(duration.as('minutes') % 60),
         seconds: Math.floor(duration.as('seconds') % 60),
      });
      if (hours > 0)
         duration = span.toFormat("hh:mm:ss");
      else
         duration = span.toFormat("mm:ss");

      return duration;
   },

   Env(name) {
      let ret = "";
      switch (name) {
         case "VITE_API_URL":
            ret = resolveApiBaseUrl();
            break;
         case "VITE_ADMIN_URL":
            ret = import.meta.env.VITE_ADMIN_URL;
            break;
         case "VITE_ENV":
            ret = import.meta.env.VITE_ENV;
            break;
         case "VITE_TEST_RUNNER_URL":
            ret = import.meta.env.VITE_TEST_RUNNER_URL;
            break;
         case "VITE_APP_INSIGHTS_KEY":
            ret = import.meta.env.VITE_APP_INSIGHTS_KEY;
            break;
         // default:
         //    debugger;
      }
      return ret;
   },

   getBrowserInfo() {
      const userAgent = navigator.userAgent;
      const userAgentData = navigator.userAgentData || {};
      const platform = userAgentData.platform || navigator.platform;
      const browser = this.getBrowserName(userAgent);
      const os = this.getOSName(platform, userAgent);

      return { browser, os };
   },

   getBrowserName(userAgent) {
      let browserName = "Unknown";
      let browserVersion = "";

      if (userAgent.indexOf("Firefox") > -1) {
         browserName = "Firefox";
         browserVersion = userAgent.match(/Firefox\/(\d+\.\d+)/)[1];
      } else if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) {
         browserName = "Opera";
         browserVersion = userAgent.match(/(Opera|OPR)\/(\d+\.\d+)/)[2];
      } else if (userAgent.indexOf("Trident") > -1) {
         browserName = "Internet Explorer";
         browserVersion = userAgent.match(/rv:(\d+\.\d+)/)[1];
      } else if (userAgent.indexOf("Edge") > -1) {
         browserName = "Edge";
         browserVersion = userAgent.match(/Edge\/(\d+\.\d+)/)[1];
      } else if (userAgent.indexOf("Chrome") > -1) {
         browserName = "Chrome";
         browserVersion = userAgent.match(/Chrome\/(\d+\.\d+)/)[1];
      } else if (userAgent.indexOf("Safari") > -1) {
         browserName = "Safari";
         browserVersion = userAgent.match(/Version\/(\d+\.\d+)/)[1];
      }

      return `${browserName} ${browserVersion}`;
   },

   getOSName(platform, userAgent) {
      let osName = "Unknown";

      if (platform.indexOf("Win") > -1) {
         osName = "Windows";
      } else if (platform.indexOf("Mac") > -1) {
         osName = "MacOS";
      } else if (platform.indexOf("Linux") > -1) {
         osName = "Linux";
      } else if (/Android/.test(userAgent)) {
         osName = "Android";
      } else if (/iPhone|iPad|iPod/.test(userAgent)) {
         osName = "iOS";
      }

      return osName;
   },

   /**
    * Capitalizes the first letter of a string
    * @param {string} str - The string to capitalize
    * @returns {string} The string with the first letter capitalized
    */
   capitalizeFirstLetter(str) {
      if (!str || typeof str !== 'string' || str.length === 0) return str;
      return str.charAt(0).toUpperCase() + str.slice(1);
   },

   JobEfDescription(job) {
      var descriptions = job.descriptions
         // .filter(d => d.stateId == Enums.JOB_DESCRIPTION_STATE.DescriptionApproved)
         .sort((a, b) => {
            // Reverse-chronological: newest first
            // Use Luxon if available in this file's scope; otherwise fallback to Date
            try {
               const aDt = DateTime.fromISO(a.dateCreated);
               const bDt = DateTime.fromISO(b.dateCreated);
               if (aDt.isValid && bDt.isValid) {
                  return bDt.toMillis() - aDt.toMillis();
               }
            } catch { }
            const aMs = new Date(a.dateCreated).getTime();
            const bMs = new Date(b.dateCreated).getTime();
            return bMs - aMs;
         });
      if (descriptions.length === 0) return null;
      return descriptions[0];
   },

   JobDescription(job) {
      var descriptions = job.descriptions
         .filter(d => d.stateId !== Enums.JOB_DESCRIPTION_STATE.Deleted &&
            d.stateId !== Enums.JOB_DESCRIPTION_STATE.Archived)
         .sort((a, b) => {
            // Reverse-chronological: newest first
            // Use Luxon if available in this file's scope; otherwise fallback to Date
            try {
               const aDt = DateTime.fromISO(a.dateCreated);
               const bDt = DateTime.fromISO(b.dateCreated);
               if (aDt.isValid && bDt.isValid) {
                  return bDt.toMillis() - aDt.toMillis();
               }
            } catch { }
            const aMs = new Date(a.dateCreated).getTime();
            const bMs = new Date(b.dateCreated).getTime();
            return bMs - aMs;
         });
      if (descriptions.length === 0) return null;
      return descriptions[0];
   },

   DescriptionStateName(job) {
      if (!job) return '';
      var description = Helpers.JobDescription(job);
      if (!description) return '';
      switch (description.stateId) {
         default:
         case Enums.JOB_DESCRIPTION_STATE.Created:
         case Enums.JOB_DESCRIPTION_STATE.PendingDescription:
            return "";
         case Enums.JOB_STATE.PendingDescriptionEdit:
            return "Pending Revision";
         case Enums.JOB_DESCRIPTION_STATE.PendingDescriptionAcceptance:
            return "JD Submitted";
         case Enums.JOB_DESCRIPTION_STATE.DescriptionApproved:
            return "Approved";
         case Enums.JOB_DESCRIPTION_STATE.Archived:
            return "Archived";
         case Enums.JOB_STATE.Deleted:
            return "Deleted";
      }
   },

   JobStateName(job) {
      switch (job.stateId) {
         default:
         case Enums.JOB_STATE.PendingDescription:
            return "";
         case Enums.JOB_STATE.PendingEF:
            return "JD Submitted";
         case Enums.JOB_STATE.PendingAlgo:
            return "Active";
         case Enums.JOB_STATE.Active:
            return "Active";
         case Enums.JOB_STATE.Archived:
            return "Archived";
         case Enums.JOB_STATE.Deleted:
            return "Deleted";
      }
   },

   async downloadBlob(blobId) {
      return this.downloadBlobToFolder(blobId);
   },

   async downloadBlobToFolder(blobId) {
      if (!blobId) {
         toast.error("blobId parameter is not optional.");
         return false;
      }

      try {
         const ok = await downloadAuthenticatedFile(`/Image/${blobId}`, "download");
         if (ok) toast.success("File downloaded successfully!");
         else toast.error("Error downloading file.");
         return ok;
      } catch (error) {
         toast.error("Error downloading file.");
         console.error("Error downloading file:", error);
         return false;
      }
   },

   async downloadJobDescriptionAttachment(jobId, description) {
      return this.downloadJobDescriptionAttachmentToFolder(jobId, description);
   },

   async downloadJobDescriptionAttachmentToFolder(jobId, description) {
      const jdId = description?.id ?? description?.companyJobDescriptionId;
      if (!jobId || !jdId) {
         toast.error("Parameters are not optional.");
         return false;
      }

      try {
         const fallback = description.attachmentFileName || "download";
         const ok = await downloadAuthenticatedFile(
            `/CompanyJob/${jobId}/Description/${jdId}/File`,
            fallback
         );
         if (ok) toast.success("File downloaded successfully!");
         else toast.error("Error downloading file.");
         return ok;
      } catch (error) {
         toast.error("Error downloading file.");
         console.error("Error downloading file:", error);
         return false;
      }
   },

   normalizeJsonLikeText(value) {
      if (typeof value !== "string") return "";

      return value
         .replace(/[\u201C\u201D]/g, '"')
         .replace(/[\u2018\u2019]/g, "'")
         .replace(/([{,]\s*)([A-Za-z_$][A-Za-z0-9_$-]*)(\s*:)/g, "$1\"$2\"$3");
   },

   getJsoncErrorExplanation(errorName) {
      const explanations = {
         InvalidSymbol: "Unexpected character or token in JSON.",
         InvalidNumberFormat: "Number is malformed (for example, invalid decimal/exponent format).",
         PropertyNameExpected: "An object property name was expected (for example, missing key before ':').",
         ValueExpected: "A JSON value was expected (string, number, object, array, true, false, or null).",
         ColonExpected: "A ':' is missing between an object property name and its value.",
         CommaExpected: "A ',' is missing between array items or object properties.",
         CloseBraceExpected: "A '}' is missing to close an object.",
         CloseBracketExpected: "A ']' is missing to close an array.",
         EndOfFileExpected: "Extra content appears after valid JSON ended.",
         InvalidCommentToken: "A comment token is malformed.",
         UnexpectedEndOfComment: "A block comment was not closed properly.",
         UnexpectedEndOfString: "A string literal was not closed with a matching quote.",
         UnexpectedEndOfNumber: "A number literal ended unexpectedly.",
         InvalidUnicode: "Unicode escape sequence is invalid (for example, bad \\uXXXX).",
         InvalidEscapeCharacter: "String contains an invalid escape sequence.",
         InvalidCharacter: "Invalid character found in JSON text.",
      };

      return explanations[errorName] || "JSON syntax error reported by parser.";
   },

   getLineAndColumnFromOffset(text, offset) {
      if (typeof text !== "string" || !Number.isFinite(offset) || offset < 0) {
         return { line: null, column: null };
      }

      const safeOffset = Math.min(offset, text.length);
      const priorText = text.slice(0, safeOffset);
      const lines = priorText.split(/\r\n|\r|\n/);
      const line = lines.length;
      const column = (lines[lines.length - 1] || "").length + 1;
      return { line, column };
   },

   buildJsoncParseErrors(text, parseErrors) {
      if (!Array.isArray(parseErrors) || parseErrors.length === 0) return [];

      return parseErrors.map((parseError) => {
         const code = Number(parseError?.error);
         const errorName = printParseErrorCode(code);
         const { line, column } = this.getLineAndColumnFromOffset(text, Number(parseError?.offset || 0));
         const offset = Number(parseError?.offset || 0);
         const length = Number(parseError?.length || 1);
         const snippetStart = Math.max(0, offset - 20);
         const snippetEnd = Math.min(text.length, offset + Math.max(1, length) + 20);
         const snippet = typeof text === "string" ? text.slice(snippetStart, snippetEnd) : "";

         return {
            code,
            errorName,
            explanation: this.getJsoncErrorExplanation(errorName),
            offset,
            length,
            line,
            column,
            snippet,
         };
      });
   },

   parseJsonLenient(value) {
      if (typeof value !== "string" || !value.trim()) {
         return { ok: false, error: "JSON is empty.", parseErrors: [] };
      }

      // Parse the raw text first so valid JSON string content (including smart quotes)
      // is not altered by lenient normalization heuristics.
      try {
         return { ok: true, parsed: JSON.parse(value) };
      } catch {
         // Fall through to lenient modes.
      }

      const rawParseErrors = [];

      try {
         if (typeof parseJsonc === "function") {
            const parsedRawJsonc = parseJsonc(value, rawParseErrors, {
               allowTrailingComma: true,
               disallowComments: false,
               allowEmptyContent: false,
            });

            if (!rawParseErrors.length && parsedRawJsonc !== undefined) {
               return { ok: true, parsed: parsedRawJsonc };
            }
         }
      } catch {
         // Fall through to normalization pass.
      }

      let normalized;
      try {
         normalized = this.normalizeJsonLikeText(value);
      } catch {
         return {
            ok: false,
            error: "Could not normalize JSON text.",
            parseErrors: [],
         };
      }

      const parseErrors = [];

      try {
         return { ok: true, parsed: JSON.parse(normalized) };
      } catch {
         try {
            if (typeof parseJsonc !== "function") {
               return {
                  ok: false,
                  error: "Could not auto-correct JSON. Please fix syntax and try again.",
                  parseErrors: [],
               };
            }

            const parsed = parseJsonc(normalized, parseErrors, {
               allowTrailingComma: true,
               disallowComments: false,
               allowEmptyContent: false,
            });

            if (!parseErrors.length && parsed !== undefined) {
               return { ok: true, parsed };
            }
         } catch {
            return {
               ok: false,
               error: "Could not auto-correct JSON. Please fix syntax and try again.",
               parseErrors: [],
            };
         }

         const detailedParseErrors = this.buildJsoncParseErrors(normalized, parseErrors);
         return {
            ok: false,
            error: "Could not auto-correct JSON. Please fix syntax and try again.",
            parseErrors: detailedParseErrors,
         };
      }
   },

   formatJsonSmart(value, maxLineLength = 200, indentSize = 2) {
      const indentUnit = " ".repeat(indentSize);

      const isPrimitiveJsonValue = (v) => v === null || ["string", "number", "boolean"].includes(typeof v);
      const isLeafJsonNode = (v) => {
         if (Array.isArray(v)) {
            return v.every((item) => isPrimitiveJsonValue(item));
         }
         if (v && typeof v === "object") {
            return Object.values(v).every((item) => isPrimitiveJsonValue(item));
         }
         return false;
      };

      function render(node, indentLevel) {
         if (isPrimitiveJsonValue(node)) {
            return JSON.stringify(node);
         }

         if (isLeafJsonNode(node)) {
            const singleLine = JSON.stringify(node);
            if ((indentLevel * indentSize) + singleLine.length <= maxLineLength) {
               return singleLine;
            }
         }

         if (Array.isArray(node)) {
            if (node.length === 0) return "[]";

            const nextIndent = indentUnit.repeat(indentLevel + 1);
            const currentIndent = indentUnit.repeat(indentLevel);
            const lines = node.map((item) => `${nextIndent}${render(item, indentLevel + 1)}`);
            return `[\n${lines.join(",\n")}\n${currentIndent}]`;
         }

         if (node && typeof node === "object") {
            const entries = Object.entries(node);
            if (!entries.length) return "{}";

            const nextIndent = indentUnit.repeat(indentLevel + 1);
            const currentIndent = indentUnit.repeat(indentLevel);
            const lines = entries.map(([key, val]) => (
               `${nextIndent}${JSON.stringify(key)}: ${render(val, indentLevel + 1)}`
            ));

            return `{\n${lines.join(",\n")}\n${currentIndent}}`;
         }

         return JSON.stringify(node);
      }

      return render(value, 0);
   },

   formatJsonForEditor(value, maxLineLength = 200, indentSize = 2) {
      if (typeof value === "string") {
         const result = this.parseJsonLenient(value);
         if (result.ok) return this.formatJsonSmart(result.parsed, maxLineLength, indentSize);
         return value;
      }

      if (value === null || value === undefined) return "";
      return this.formatJsonSmart(value, maxLineLength, indentSize);
   },

   tryParseJson(value) {
      if (typeof value !== "string") return null;
      const trimmed = value.trim();
      if (!trimmed) return null;
      if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return null;
      try {
         return JSON.parse(trimmed);
      } catch {
         return null;
      }
   },

   isJsonString(value) {
      return this.tryParseJson(value) !== null;
   },
   shouldRenderContentAsHtml(value) {
      if (typeof value !== "string") return false;
      const trimmed = value.trim();
      if (!trimmed) return false;
      return /<\s*\/?\s*[a-z][\w:-]*(?:\s+[^<>]*?)?\/?\s*>/i.test(trimmed);
   },

   isHtmlString(value) {
      return this.shouldRenderContentAsHtml(value);
   },

   decodeBase64Text(value) {
      if (typeof value !== "string" || !value.trim()) return "";
      try {
         const binary = atob(value);
         const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
         if (typeof TextDecoder !== "undefined") {
            const looksLikeUtf16Le = bytes.length > 1 && bytes.length % 2 === 0 && (() => {
               let zeroHighBytes = 0;
               let pairs = 0;
               for (let i = 1; i < bytes.length; i += 2) {
                  pairs++;
                  if (bytes[i] === 0) zeroHighBytes++;
               }
               return pairs > 0 && (zeroHighBytes / pairs) > 0.4;
            })();

            if (looksLikeUtf16Le) {
               return new TextDecoder("utf-16le").decode(bytes);
            }

            return new TextDecoder("utf-8").decode(bytes);
         }
      } catch {
         // Fall through to legacy decode below.
      }

      try {
         return decodeURIComponent(escape(atob(value)));
      } catch {
         return value;
      }
   },

   encodeBase64Text(value) {
      if (typeof value !== "string") return "";
      try {
         if (typeof TextEncoder !== "undefined") {
            const bytes = new TextEncoder().encode(value);
            let binary = "";
            for (let i = 0; i < bytes.length; i++) {
               binary += String.fromCharCode(bytes[i]);
            }
            return btoa(binary);
         }
      } catch {
         // Fall through to legacy encode below.
      }

      try {
         return btoa(unescape(encodeURIComponent(value)));
      } catch {
         return "";
      }
   },

   decodeBase64Unicode(value) {
      return this.decodeBase64Text(value);
   },

   encodeBase64Unicode(value) {
      return this.encodeBase64Text(value);
   },

   tryMinifyJson(value) {
      const parsed = this.tryParseJson(value);
      return parsed === null ? value : JSON.stringify(parsed);
   },
   deepClone(value) {
      return JSON.parse(JSON.stringify(value ?? null));
   },

   /**
    * Canonical way to get parsed content for a section given a language abbrev.
    * Reads from section.contents (Record<abbrev, contentString>).
    * Falls back to 'eng' if the requested language is missing.
   * Returns schema-v2 shape: { schemaVersion, elements, html, htmlDecoded }.
    */
   getSectionContent(section, lang) {
      const contents = section?.contents;
      if (!contents || typeof contents !== "object") return {};
      const raw = contents[lang] ?? contents["eng"] ?? Object.values(contents)[0] ?? "";
      if (!raw) return {};
      const parsed = typeof raw === "object" ? raw : this.tryParseJson(raw);
      if (parsed === null) {
         const plainText = typeof raw === "string" ? raw : String(raw ?? "");
         return {
            schemaVersion: 2,
            elements: {},
            html: {},
            htmlDecoded: {
               instructions: plainText,
               instructionsSecondary: "",
               additionalInstructions: "",
               reminder: "",
            },
         };
      }

      const elements = this.normalizeSectionElements(parsed);
      const html = (parsed?.html && typeof parsed.html === "object" && !Array.isArray(parsed.html))
         ? parsed.html
         : {};

      return {
         ...parsed,
         schemaVersion: Number(parsed?.schemaVersion || 2),
         elements,
         html,
         htmlDecoded: Object.keys(html).reduce((acc, key) => {
            acc[key] = this.decodeBase64Text(html?.[key]);
            return acc;
         }, {}),
      };
   },

   normalizeSectionElements(parsed) {
      if (!parsed || typeof parsed !== "object") return {};

      const rawElements = parsed.elements;
      if (Array.isArray(rawElements)) {
         const normalized = { items: rawElements };
         if (Array.isArray(parsed.headers)) normalized.headers = parsed.headers;
         return normalized;
      }

      if (rawElements && typeof rawElements === "object") {
         return rawElements;
      }

      if (Array.isArray(parsed.items)) {
         return { items: parsed.items };
      }

      if (Array.isArray(parsed.adjectives)) {
         return { items: parsed.adjectives };
      }

      if (Array.isArray(parsed.values)) {
         return { items: parsed.values };
      }

      return {};
   },

   getSectionElements(content) {
      if (!content || typeof content !== "object") return {};
      const elements = content?.elements;
      if (!elements || typeof elements !== "object" || Array.isArray(elements)) return {};
      return elements;
   },

   colorsheetColorKeys: ["yellow", "red", "blue", "green"],

   resolveColorsheetColorKey(square, index) {
      const raw = String(square?.color || "").trim().toLowerCase();
      if (this.colorsheetColorKeys.includes(raw)) return raw;
      const fromIndex = this.colorsheetColorKeys[Number(index) - 1];
      return fromIndex || raw;
   },

   getColorsheetSquareHtml(content, square, index) {
      const canonicalKey = this.resolveColorsheetColorKey(square, index);
      const rawKey = String(square?.color || "").trim().toLowerCase();
      const fromHtml = this.getSectionHtmlValue(content, canonicalKey)
         || (rawKey && rawKey !== canonicalKey ? this.getSectionHtmlValue(content, rawKey) : "");
      if (fromHtml) return fromHtml;
      return typeof square?.text === "string" ? square.text : "";
   },

   getSectionHtmlValue(content, key) {
      if (typeof key !== "string" || !key) return "";
      const decoded = content?.htmlDecoded;
      let value = decoded?.[key];
      if ((value === undefined || value === null || value === "") && key === "instructionsSecondary") {
         value = decoded?.instructions1;
      }
      if (value === undefined || value === null || value === "") {
         const elements = content?.elements;
         if (elements && typeof elements === "object" && typeof elements[key] === "string") {
            value = elements[key];
         } else if (key === "instructions" && typeof content?.instructions === "string") {
            value = content.instructions;
         }
      }
      return typeof value === "string" ? value : "";
   },

   pickControlText(...candidates) {
      for (const candidate of candidates) {
         if (typeof candidate === "string" && candidate.trim() !== "") {
            return candidate;
         }
      }
      return "";
   },

   normalizeSectionControlElements(raw) {
      if (!raw || typeof raw !== "object") {
         return { buttons: {}, errors: {}, labels: {} };
      }
      const buttons = raw.buttons || raw.Buttons || {};
      const errors = raw.errors || raw.Errors || {};
      const labels = raw.labels || raw.Labels || {};
      return {
         buttons: {
            nextPage: buttons.nextPage ?? buttons.NextPage ?? "",
            nextSection: buttons.nextSection ?? buttons.NextSection ?? "",
            complete: buttons.complete ?? buttons.Complete ?? "",
         },
         errors: {
            missing: errors.missing ?? errors.Missing ?? "",
         },
         labels: {
            pageLabel: labels.pageLabel ?? labels.PageLabel ?? "",
         },
      };
   },

   resolveSectionControlElements(sectionControls, sectionControlsByAbbrev, section, lang) {
      const catalog = this.normalizeSectionControlElements(sectionControls);
      const abbrevMap = sectionControlsByAbbrev && typeof sectionControlsByAbbrev === "object"
         ? sectionControlsByAbbrev
         : {};
      const englishCatalog = this.normalizeSectionControlElements(abbrevMap.eng ?? abbrevMap.Eng);
      const sectionContent = this.getSectionContent(section, lang || "eng");
      // Prefer top-level elements.*; also accept mistaken elements.elements.*
      // from the section editor when users pasted a full-schema wrapper.
      const sectionElements = this.getSectionElements(sectionContent);
      const nestedElements = sectionElements?.elements && typeof sectionElements.elements === "object"
         && !Array.isArray(sectionElements.elements)
         ? sectionElements.elements
         : null;
      const sectionButtons = (sectionElements?.buttons && typeof sectionElements.buttons === "object"
         ? sectionElements.buttons
         : null)
         || (nestedElements?.buttons && typeof nestedElements.buttons === "object"
            ? nestedElements.buttons
            : {})
         || {};
      const sectionErrors = (sectionElements?.errors && typeof sectionElements.errors === "object"
         ? sectionElements.errors
         : null)
         || (nestedElements?.errors && typeof nestedElements.errors === "object"
            ? nestedElements.errors
            : {})
         || {};
      const sectionLabels = (sectionElements?.labels && typeof sectionElements.labels === "object"
         ? sectionElements.labels
         : null)
         || (nestedElements?.labels && typeof nestedElements.labels === "object"
            ? nestedElements.labels
            : {})
         || {};
      const sectionPageLabel = typeof sectionElements?.pageLabel === "string"
         ? sectionElements.pageLabel
         : (typeof nestedElements?.pageLabel === "string" ? nestedElements.pageLabel : "");

      return {
         buttons: {
            nextPage: this.pickControlText(
               sectionButtons.nextPage,
               catalog.buttons.nextPage,
               englishCatalog.buttons.nextPage,
            ),
            nextSection: this.pickControlText(
               sectionButtons.nextSection,
               catalog.buttons.nextSection,
               englishCatalog.buttons.nextSection,
            ),
            complete: this.pickControlText(
               sectionButtons.complete,
               catalog.buttons.complete,
               englishCatalog.buttons.complete,
            ),
         },
         errors: {
            missing: this.pickControlText(
               sectionErrors.missing,
               catalog.errors.missing,
               englishCatalog.errors.missing,
            ),
         },
         labels: {
            pageLabel: this.pickControlText(
               sectionLabels.pageLabel,
               sectionPageLabel,
               catalog.labels.pageLabel,
               englishCatalog.labels.pageLabel,
            ),
         },
      };
   },

   buildSectionControlsByAbbrevFromRows(rows) {
      if (!Array.isArray(rows)) return {};
      const entriesByAbbrev = {};
      for (const row of rows) {
         const abbrev = row?.languageAbbrev ?? row?.LanguageAbbrev
            ?? row?.abbrev ?? row?.Abbrev;
         const languageId = Number(row?.languageId ?? row?.LanguageId ?? 0);
         let resolvedAbbrev = typeof abbrev === "string" ? abbrev : "";
         if (!resolvedAbbrev && languageId === 1) resolvedAbbrev = "eng";
         if (!resolvedAbbrev) continue;

         if (!entriesByAbbrev[resolvedAbbrev]) {
            entriesByAbbrev[resolvedAbbrev] = { buttons: {}, errors: {}, labels: {} };
         }
         const category = String(row?.category ?? row?.Category ?? "").toLowerCase();
         const key = String(row?.key ?? row?.Key ?? "").toLowerCase();
         const content = String(row?.content ?? row?.Content ?? "");
         if (!content) continue;

         if (category === "buttons") {
            if (key === "nextpage") entriesByAbbrev[resolvedAbbrev].buttons.nextPage = content;
            else if (key === "nextsection") entriesByAbbrev[resolvedAbbrev].buttons.nextSection = content;
            else if (key === "complete") entriesByAbbrev[resolvedAbbrev].buttons.complete = content;
         } else if (category === "errors" && key === "missing") {
            entriesByAbbrev[resolvedAbbrev].errors.missing = content;
         } else if (category === "labels" && key === "pagelabel") {
            entriesByAbbrev[resolvedAbbrev].labels.pageLabel = content;
         }
      }
      return entriesByAbbrev;
   },
};

export default Helpers;
